import type { CityConfig } from "../city-template/types";

export const vacavilleConfig: CityConfig = {
  city: "Vacaville",
  state: "California",
  stateAbbr: "CA",
  slug: "vacaville",
  route: "/cities/vacaville",
  title: "Emergency Passport Vacaville, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Vacaville, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Solano County and the Sacramento Valley. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Vacaville, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Vacaville and Solano County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Vacaville%20California%20suburban%20cityscape%20at%20golden%20hour%20with%20rolling%20green%20hills%20and%20vineyards%20in%20the%20background%2C%20modern%20residential%20neighborhoods%20and%20retail%20centers%2C%20warm%20sunset%20light%20over%20the%20Sacramento%20Valley%2C%20Travis%20Air%20Force%20Base%20visible%20in%20the%20distance%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20clean%20and%20peaceful%20suburban%20California%20scenery&width=1800&height=700&seq=vacaville-hero-001&orientation=landscape",
  heroImageAlt: "Vacaville California with rolling green hills, vineyards, and suburban neighborhoods at golden hour",
  geoPosition: "38.3566;-121.9877",
  geoPlacename: "Vacaville, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in downtown SF — approximately 45–60 minutes from Vacaville via I-80",
  },
  nearbyCitiesZips: [
    { name: "Vacaville", zips: "95687, 95688, 95696" },
    { name: "Fairfield", zips: "94533, 94534, 94535" },
    { name: "Dixon", zips: "95620" },
    { name: "Suisun City", zips: "94585" },
    { name: "Benicia", zips: "94510" },
    { name: "Vallejo", zips: "94589–94592" },
    { name: "Napa", zips: "94558, 94559" },
    { name: "American Canyon", zips: "94503" },
    { name: "Davis", zips: "95616–95618" },
    { name: "Woodland", zips: "95695, 95776" },
    { name: "Sacramento", zips: "95811–95835" },
    { name: "West Sacramento", zips: "95691" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Solano County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "Vallejo", route: "/cities/vallejo" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Oakland", route: "/cities/oakland" },
    { name: "Berkeley", route: "/cities/berkeley" },
    { name: "Davis", route: "/davis" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Vacaville remotely via secure courier. We do not have a walk-in office in Vacaville — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Solano County travelers.",
  fpcOfficeDistance: "Serving all of Solano County",
  serviceAreaIntro: "From the Nut Tree to the Premium Outlets, from the rolling hills of Green Valley to the Travis Air Force Base community — Fast Passport Center serves the entire Vacaville area. We regularly help travelers from Fairfield, Dixon, Suisun City, and Benicia get their passports fast.",
  nearbyCitiesIntro: "We serve all Vacaville neighborhoods and nearby Solano County cities including Fairfield, Dixon, Suisun City, Benicia, and Vallejo. If you are in Solano, Napa, or Yolo County, we have you covered.",
  howItWorksIntro: "Most Vacaville travelers complete everything online and ship documents overnight. No need to drive to Sacramento or San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Vacaville situated between the Bay Area and Sacramento, travelers often split their time between regions. Here are the most common passport mistakes we see from Solano County applicants.",
  testimonialsIntro: "Vacaville and Solano County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Vacaville? Here are the most common ones we hear from Solano County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Solano County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Thomas H.",
      location: "Vacaville, CA",
      rating: 5,
      text: "My business trip to Vancouver was in 3 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Incredible service.",
      service: "Emergency Passport",
    },
    {
      name: "Maria S.",
      location: "Fairfield, CA",
      rating: 5,
      text: "Needed to renew for a family cruise to Mexico. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin J.",
      location: "Dixon, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Hawaii. They walked me through the DS-64 process and got me a replacement in 2 days. Saved our trip.",
      service: "Lost Passport",
    },
    {
      name: "Patricia L.",
      location: "Benicia, CA",
      rating: 5,
      text: "As a military spouse at Travis AFB, I need reliable passport service. Fast Passport Center has been my go-to for years. Professional and fast every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "vacaville-1",
      name: "Vacaville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 Ulatis Dr",
      city: "Vacaville",
      state: "CA",
      zip: "95687",
      phone: "(707) 448-4423",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Vacaville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "vacaville-2",
      name: "Fairfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1425 W Texas St",
      city: "Fairfield",
      state: "CA",
      zip: "94533",
      phone: "(707) 425-6511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles from Vacaville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "vacaville-3",
      name: "Dixon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "135 E A St",
      city: "Dixon",
      state: "CA",
      zip: "95620",
      phone: "(707) 678-1234",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles west of Vacaville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for western Solano County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "vacaville-4",
      name: "Vallejo Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 Vervais Ave",
      city: "Vallejo",
      state: "CA",
      zip: "94589",
      phone: "(707) 642-4373",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "20 miles west of Vacaville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for western Solano County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "vacaville-5",
      name: "Solano County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "675 Texas St",
      city: "Fairfield",
      state: "CA",
      zip: "94533",
      phone: "(707) 784-6205",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5 miles from Vacaville",
      website: "https://www.solanocounty.com/",
      websiteLabel: "Solano County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "vacaville-6",
      name: "Napa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1532 1st St",
      city: "Napa",
      state: "CA",
      zip: "94559",
      phone: "(707) 255-5181",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles north of Vacaville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Napa County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Vacaville photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Solano County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Vacaville applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Vacaville families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Vacaville travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Vacaville." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Vacaville address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Vacaville before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Vacaville for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Solano County travelers." },
    { q: "How fast can I renew my passport in Vacaville?", a: "Fast Passport Center offers fast passport renewal in Vacaville in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Vacaville in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Vacaville?", a: "Same-day passport appointments require a drive to the SF Regional Passport Agency. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Vacaville residents." },
    { q: "Can I expedite a child passport in Vacaville?", a: "Yes. Fast Passport Center expedites child passports in Vacaville for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Vacaville with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Vacaville?",
      a: "With Fast Passport Center's emergency courier service, Vacaville travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to San Francisco for a passport?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Vacaville?",
      a: "Popular options include the Vacaville Post Office (95687), Fairfield Post Office (94533), Solano County Clerk (94533), and Dixon Post Office (95620). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Vacaville?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live near Travis AFB or in the military community?",
      a: "We serve the Travis Air Force Base community and all of Solano County. Our overnight shipping labels work from any UPS location, and we understand military travel requirements. Contact us for special accommodations for military personnel.",
    },
    {
      q: "How much does expedited passport service cost in Vacaville?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Vacaville?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Vacaville and Fairfield offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Vacaville?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Vacaville?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Vacaville families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Vacaville", address: "2010 Nut Tree Rd, Vacaville, CA 95687", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Vacaville", address: "1620 E Monte Vista Ave, Vacaville, CA 95688", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Vacaville", address: "1661 E Monte Vista Ave, Vacaville, CA 95688", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Fairfield", address: "1320 W Texas St, Fairfield, CA 94533", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Vacaville", address: "1501 Helen Power Dr, Vacaville, CA 95687", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Vacaville", address: "1501 E Monte Vista Ave, Vacaville, CA 95688", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Costco — Fairfield", address: "2400 Technology Ct, Fairfield, CA 94533", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
    { name: "CVS Pharmacy — Dixon", address: "1000 E D St, Dixon, CA 95620", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Vacaville",
      address: "621 Orange Dr, Vacaville, CA 95687",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Fairfield",
      address: "1501 N Texas St, Fairfield, CA 94533",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Davis",
      address: "520 3rd St, Davis, CA 95616",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Vallejo",
      address: "200 Missouri St, Vallejo, CA 94590",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for western Solano County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "35 miles east of Vacaville",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "The primary international airport for the Sacramento Valley. Major flights to Mexico, Canada, and international connections.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "45 miles south of Vacaville",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-80.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "40 miles south of Vacaville",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Convenient for Bay Area travelers. International flights to Mexico, Europe, and Asia.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
  ],
};